import {
  MutableRefObject,
  DragEvent,
  useState,
  useRef,
  useEffect,
} from 'react';
import { PDFDocument } from 'pdf-lib';
import styled, { css } from 'styled-components';
import { Pdf } from '../Pdf';
import { Tooltip } from '../Tooltip';

export const BorderCss = css<{styles?: string, hasError?: boolean}>` ${({ theme, hasError = false }) => `
  border: 1px dashed ${hasError ? theme.textDanger : theme.borderDark};
`}`;

const Center = styled.div`
  text-align:center;
  height:100%;
  width: 100%;
`;

const Container = styled.div`
  height:100%;
  width: 100%;
  display:inline-block;
`;

const StyledBorderDiv = styled.div<{hasError: boolean}>`
  ${BorderCss};
  border-radius: ${({ theme }) => theme.borderRadius[1]};
  width: 100%;
  height: 200px;
  display: flex;
  justify-content: center;
`;

const StyledNoneBorder = styled.div<{hasError: boolean}>`
  width: 100%;
  display: flex;
  justify-content: center;
  border-radius: ${({ theme }) => theme.borderRadius[1]};
`;

const FileInput = styled.input`
  display: none;
`;

const FileDragAndDropInitial = styled.div<{hasError: boolean}>`
  border-radius: ${({ theme }) => theme.borderRadius[1]};
  height:100%;
  width:100%;
`;

const FileDragAndDrop = styled.div<{hasError: boolean}>`
  ${BorderCss};
  border-radius: ${({ theme }) => theme.borderRadius[1]};
  height:100%;
  width:100%;
`;

const StyledParagraph = styled.p<{sizeSmall?: boolean, fontWeight400?: boolean}>` ${({ theme, sizeSmall = false, fontWeight400 = false }) => `
  font-weight: ${fontWeight400 ? theme.fontWeight.normal : theme.fontWeight.bold};
  padding: ${theme.padding[2]};
  text-align: center;
  font-size: ${sizeSmall ? theme.fontSize[1] : theme.fontSize[2]};
`}`;

const StyledDivCenter = styled.div`
  display: block;

  p {
    font-size: ${({ theme }) => theme.fontSize[2]};
    text-align: center;
  }
`;

const FileInputContent = styled.div`${({ theme }) => `
  margin: ${theme.padding[2]};
  display: inline-block;
  width: 180px;
  height: 200px;
  border: 1px dashed ${theme.borderFaint};
  border-radius: ${theme.borderRadius[1]};
`}`;

const FileInputContentInitial = styled.div`
  width: 740px;
  height: auto;
`;

const PlusIcon = styled.p`
  font-size: ${({ theme }) => theme.fontSize[7]};
  padding-top: 50px;
`;

const StyledContentFileUpload = styled.div`
  display: inline-block;
`;

const StyledErrorMessage = styled.div`${({ theme }) => `
  font-size: ${theme.fontSize[1]};
  font-weight: ${theme.fontWeight.normal};
  color: ${theme.textDanger};
  text-align: left;
  padding-top: ${theme.padding[4]};

  div {
    display: 'block'
  }
`}`;

const StyledUploadRow = styled.div`
  display: flex;
  flex-basis: auto;
  flex-direction: row;
  flex-wrap: wrap;
`;

const StyledUploadCol = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
`;

const StyledInstructionText = styled.a` ${({ theme }) => `
  text-decoration: underline;
  font-size: ${theme.fontSize[1]};
`}`;

export const NewSessionStateEnum = {
  SESSION_INFORMATION: 'SESSION_INFORMATION',
};

export type FileListType = {
  fileId: string,
  file: any
}

const fileSizeLimit = 200; // 200MB
const allowedTypes = ['pdf'];

const getFileExtension = (file: File) => file.name.split('.').pop()?.toLocaleLowerCase() || '';

const scanInvalidExtension = (fileList: FileListType[]) => fileList.filter((fileItem: FileListType) => !allowedTypes.includes(getFileExtension(fileItem.file)));

const scanInvalidSize = (fileList: FileListType[]) => {
  const fileSizeByByte = fileSizeLimit * 1024 * 1024;
  return fileList.filter((fileItem: FileListType) => fileItem.file.size > fileSizeByByte);
};

export type UploadType = {
  documentType: string,
  documentId: string,
  filename: string,
  base64: string,
}

export function Upload(
  {
    fileTypeOptions = {},
    upload,
    removeFile,
    maximumFiles,
  }:
  {
    fileTypeOptions?: {[key: string]: string},
    upload: (fileUpload: UploadType[]) => {},
    removeFile: (remainFiles: FileListType[]) => {},
    maximumFiles: number
  },
) {
  const [selectedFiles, setSelectedFiles] = useState<FileListType[]>([]);
  const [pageCounts, setPageCounts] = useState<{[key: string] : number}>({});
  const [pageTypes, setPageTypes] = useState<{[documentKey: string]:{[pageKey: string]: string}}>({});
  const [validationFile, setValidationFile] = useState<boolean>(false);
  const [validationMaximumFile, setValidationMaximumFile] = useState<boolean>(false);
  const fileInputRef = useRef() as MutableRefObject<HTMLInputElement>;

  const onClickFileDragAndDrop = () => {
    fileInputRef.current.click();
  };

  const createFile = async (id: string, filename: string, file: PDFDocument, documentType: string): Promise<UploadType> => {
    const base64 = `${await file.saveAsBase64()}`;

    return {
      documentType,
      documentId: id,
      filename,
      base64,
    };
  };

  const onFileUpload = async () => {
    const files = await Promise.all(selectedFiles.map(async (selectedFile, index) => {
      const file: PDFDocument = await PDFDocument.load(await selectedFile.file.arrayBuffer());
      return createFile(index.toString(), selectedFile.file.name, file, pageTypes[index]?.[0] ?? '0');
    }));

    upload(files.flat());
  };

  const onDocumentLoadSuccess = (filename: string) => ({ numPages }: { numPages:number }) => {
    setPageCounts((prevPageCounts) => ({
      ...prevPageCounts,
      [filename]: 1,
    }));

    setPageTypes((prevPageTypes) => ({
      ...prevPageTypes,
      [filename]: [...Array(numPages)].reduce((prev, _current, index) => ({
        ...prev,
        [index]: Object.keys(fileTypeOptions)[0],
      }), {}),
    }));
  };

  const handleDragAndDrop = (event: DragEvent<HTMLInputElement>) => {
    event.preventDefault();
    event.stopPropagation();
  };

  const handleSelectFiles = (files: any) => {
    setValidationMaximumFile((files.length + selectedFiles.length) > maximumFiles);

    if ((files.length + selectedFiles.length) > maximumFiles) return;

    const fileList: FileListType[] = [];

    Array.from(files).forEach((element: any, index) => {
      fileList.push({
        fileId: (selectedFiles.length + index).toString(),
        file: element,
      });
    });

    if (scanInvalidExtension(fileList).length > 0 || scanInvalidSize(fileList).length > 0) {
      setValidationFile(true);
      return;
    }

    setValidationFile(false);
    setSelectedFiles(selectedFiles.concat(fileList));
  };

  const handleDrop = (event: DragEvent<HTMLInputElement>) => {
    handleDragAndDrop(event);

    handleSelectFiles(event.dataTransfer.files);

    event.dataTransfer.clearData();
  };

  const handleDeleteFile = (selectedFileIndex: any) => {
    const remainingFileList: FileListType[] = [];

    selectedFiles.splice(selectedFileIndex, 1);

    selectedFiles.forEach((element: any, index) => {
      remainingFileList.push({
        fileId: index.toString(),
        file: element.file,
      });
    });

    setSelectedFiles(remainingFileList);
    removeFile(remainingFileList);
  };

  useEffect(() => {
    onFileUpload();
  }, [selectedFiles, pageTypes]);

  const [Wrapper, WrapperDragAndDrop, WrapperContent] = selectedFiles.length === 0 ? [FileInputContentInitial, FileDragAndDropInitial, StyledBorderDiv] : [FileInputContent, FileDragAndDrop, StyledNoneBorder];
  const dataTip = `- Upload maximum of ${maximumFiles} files<br>- Files must be in PDF format<br>- Maximum individual file size is 200 MB`;
  return (
    <Center>
      <Container>
        <WrapperContent hasError={(validationFile || validationMaximumFile) && selectedFiles.length === 0}>
          {selectedFiles && Array.from(selectedFiles).map((selectedFile) => (
            <StyledUploadRow key={`row-${selectedFile.fileId}`}>
              <StyledUploadCol>
                <Pdf
                  key={`pdf-${selectedFile.fileId}`}
                  onDocumentLoadSuccess={onDocumentLoadSuccess(selectedFile.fileId)}
                  selectedFile={selectedFile.file}
                  selectedFileIndex={selectedFile.fileId}
                  pageCount={pageCounts}
                  pageTypes={pageTypes}
                  setPageTypes={setPageTypes}
                  fileTypeOptions={fileTypeOptions}
                  handleDeleteFile={handleDeleteFile}
                  showWrapper
                />
              </StyledUploadCol>
            </StyledUploadRow>
          ))}
          {selectedFiles.length < maximumFiles && (
            <Wrapper>
              <FileInput
                id="file-input"
                ref={fileInputRef}
                type="file"
                onChange={(event: any) => handleSelectFiles(event.target.files)}
                multiple
              />
              <WrapperDragAndDrop
                hasError={(validationFile || validationMaximumFile)}
                onClick={onClickFileDragAndDrop}
                onDrop={handleDrop}
                onDragOver={handleDragAndDrop}
                onDragEnter={handleDragAndDrop}
                onDragLeave={handleDragAndDrop}
              >
                <PlusIcon><i className="mi-add" /></PlusIcon>
                {selectedFiles.length === 0 ? (
                  <StyledContentFileUpload>
                    <StyledParagraph>Click to browse or drag and drop your file.</StyledParagraph>
                    <StyledParagraph sizeSmall fontWeight400>
                      { `Upload maximum of ${maximumFiles} files, Files must be in PDF format, Maximum individual file size is 200 MB` }
                    </StyledParagraph>
                  </StyledContentFileUpload>
                ) : (
                  <StyledDivCenter>
                    <p>Upload File</p>
                  </StyledDivCenter>
                )}
              </WrapperDragAndDrop>
            </Wrapper>
          )}
        </WrapperContent>
        {(validationFile || validationMaximumFile) && (
          <StyledErrorMessage>
            Invalid file uploaded. Please read the
            {' '}
            <StyledInstructionText
              id="instruction-tooltip"
            >
              instructions.
            </StyledInstructionText>
            <Tooltip id="instruction-tooltip">{dataTip}</Tooltip>
          </StyledErrorMessage>
        )}
      </Container>
    </Center>
  );
}
